/**
 * Shared helpers for panel widths that are dragged by {@link ResizeHandleComponent} and remembered per browser.
 *
 * Kept as free functions rather than a service so the clamping rule — the thing that actually has to be right —
 * is testable without a TestBed, and so both hosts (the Operator nav rail and the Offene-Tische list panel)
 * demonstrably apply the *same* rule instead of each rolling its own.
 */

/** Clamps a width into `[min, max]`, tolerating a non-finite input by falling back to `min`. */
export function clampPanelWidth(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(Math.round(value), min), max);
}

/**
 * Reads a persisted panel width back.
 *
 * `localStorage.getItem` is **not** consent-trapped (only `setItem` is — see the privacy guard in
 * `CookieService`), so reading is unconditional and needs no cookie category. A missing, unparsable or
 * out-of-range value degrades to `fallback` rather than throwing: a remembered preference is a nicety, and a
 * panel that refuses to render because a stale string sat in storage would not be.
 *
 * @param key Storage key, e.g. `openTables.listWidth`.
 * @param fallback The width to use when nothing usable is stored.
 * @param min Lower bound — a stored value below it is clamped up, never honoured.
 * @param max Upper bound — likewise.
 */
export function readStoredPanelWidth(key: string, fallback: number, min: number, max: number): number {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return clampPanelWidth(fallback, min, max);
    const parsed = Number.parseInt(stored, 10);
    return Number.isFinite(parsed) ? clampPanelWidth(parsed, min, max) : clampPanelWidth(fallback, min, max);
  } catch {
    // Storage can be unavailable outright (Safari private mode, a locked-down profile). The panel still works;
    // it just starts at its default every time.
    return clampPanelWidth(fallback, min, max);
  }
}
